import React, { useContext, useEffect, useReducer, useRef } from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import IAuthState from "../interfaces/IAuthState";
import { authReducer } from "../reducers/authReducer";
import { isAuthIssue, queryClient } from "../queries/queryClient";
import { isTokenExpiring, registerTokenRefresher, setTokenExpiration } from "../services/tokenService";

export interface IAuthContext {
    state: IAuthState;
    dispatch: React.Dispatch<any>;
    refreshToken: () => Promise<string | undefined>;
}
const AuthContext = React.createContext<IAuthContext>({
    state: {},
    dispatch: () => null,
    refreshToken: () => Promise.resolve(undefined)
});

const authFailed = microsoftTeams.app.FailedReason.AuthFailed;

// Timers are throttled while the app is in the background, so the expiration is only checked this often
// and the check runs again as soon as the user comes back to the app
const EXPIRATION_CHECK_INTERVAL_MS = 60 * 1000;

export const AuthProvider = ({ children }: any) => {
    const [state, dispatch] = useReducer(authReducer, { authenticating: false });
    const pendingRefresh = useRef<Promise<string | undefined>>();

    // All the callers share a single refresh, no matter how many requests failed at once
    const refreshToken = (): Promise<string | undefined> => {
        if (!pendingRefresh.current) {
            dispatch({ type: "REAUTH" });
            pendingRefresh.current = acquireToken().finally(() => { pendingRefresh.current = undefined; });
        }

        return pendingRefresh.current;
    };

    const contextValue = { state, dispatch, refreshToken };

    useEffect(() => {
        registerTokenRefresher(refreshToken);
        refreshToken();

        const refreshIfExpiring = () => {
            if (isTokenExpiring()) { refreshToken(); }
        };
        const refreshIfVisible = () => {
            if (document.visibilityState === "visible") { refreshIfExpiring(); }
        };

        const interval = window.setInterval(refreshIfExpiring, EXPIRATION_CHECK_INTERVAL_MS);
        document.addEventListener("visibilitychange", refreshIfVisible);
        window.addEventListener("focus", refreshIfExpiring);
        window.addEventListener("online", refreshIfExpiring);

        return () => {
            registerTokenRefresher(undefined);
            window.clearInterval(interval);
            document.removeEventListener("visibilitychange", refreshIfVisible);
            window.removeEventListener("focus", refreshIfExpiring);
            window.removeEventListener("online", refreshIfExpiring);
        };
    }, []);

    const acquireToken = async (): Promise<string | undefined> => {
        try {
            const ssoToken = await microsoftTeams.authentication.getAuthToken();
            return await exchangeClientTokenForServerToken(ssoToken);
        } catch (error) {
            microsoftTeams.app.notifyFailure({ reason: authFailed, message: `SSO failed: ${error}` });
            console.error("SSO failed: ", error);
            dispatch({ type: "RESET_AUTH" });
            return undefined;
        }
    };

    const tokenAcquired = (accessToken: string, expiresInSeconds?: number) => {
        setTokenExpiration(accessToken, expiresInSeconds);
        dispatch({ type: "SET_TOKEN", accessToken });
        microsoftTeams.app.notifySuccess();

        // Whatever failed on the expired token loads again without the user having to do anything
        queryClient.refetchQueries({ predicate: query => isAuthIssue(query.state.error) });
    };

    //https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-oauth2-on-behalf-of-flow
    const exchangeClientTokenForServerToken = async (token: string): Promise<string | undefined> => {
        // The token goes in a header, in the URL it can exceed the server's query string limit
        const serverURL = `${import.meta.env.VITE_BASE_URL}/getAccessToken`;
        const response = await fetch(serverURL, { headers: { Authorization: `Bearer ${token}` } });
        const data = await response.json();

        if (!response.ok && data.error === "consent_required") {
            return await showConsentDialog();
        } else if (!response.ok) {
            microsoftTeams.app.notifyFailure({ reason: authFailed, message: "Nepodařilo se získat token" });
            console.error(data);
            dispatch({ type: "RESET_AUTH" });
            return undefined;
        }

        tokenAcquired(data.access_token, data.expires_in);
        return data.access_token;
    };

    //Show a popup dialogue prompting the user to consent to the required API permissions. This opens ConsentPopup.js.
    //Learn more: https://docs.microsoft.com/en-us/microsoftteams/platform/tabs/how-to/authentication/auth-tab-aad#initiate-authentication-flow
    const showConsentDialog = async (): Promise<string | undefined> => {
        try {
            const result = await microsoftTeams.authentication.authenticate({
                url: window.location.origin + "/auth-start",
                width: 600,
                height: 535
            });
            tokenAcquired(result);
            return result;
        } catch (reason) {
            console.error("Consent failed: ", reason);
            dispatch({ type: "RESET_AUTH" });
            microsoftTeams.app.notifyFailure({ reason: authFailed, message: `Consent failed: ${reason}` });
            return undefined;
        }
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    return context;
};
